// Reine Rechnung der Cluster-Seite, Stand 03 „Tabelle je Cluster"
// (Mockup docs/mockups/cluster-modern.html, Tim 29.09.2026: „Setzen wir
// die Empfehlung um"). Zustand eines Rechners in Worten und Ton, der
// Lage-Satz je Cluster, die Phase einer laufenden Aktion. Ohne Svelte,
// damit testbar (lib/rechner.test.mjs).

import type { LageTeil } from "../lage";
import type { ClusterStatsHost } from "../types";

// "ok" = Normales (StatusBadge: leise, mit „alles, auch ok" grün); "" = kein
// Urteil (schläft, stumm, unbekannt) -- bleibt immer leise.
export type ZustandTon = "" | "ok" | "warn" | "danger" | "accent";

export interface RechnerZustand {
  text: string;
  ton: ZustandTon;
}

/** Was eine laufende Aktion (Neustart, Upgrade) gerade tut. */
export interface Phase {
  label: string;
  /** danger = fehlgeschlagen, fertig = abgeschlossen (leise), sonst läuft */
  art: "laeuft" | "fertig" | "danger";
  /** Die Aktion ist ein Upgrade (sonst ein Neustart): dann gibt es einen
   *  Verlauf zum Ansehen. */
  upgrade: boolean;
}

export interface Umstaende {
  /** Offline ist für diesen Rechner normal (sleep_ok oder Override) */
  schlaeft: boolean;
  /** Der Cluster ist stummgeschaltet */
  stumm: boolean;
  phase?: Phase | null;
}

/** Phase einer Aktion aus dem Action-Store. Wörter wie bisher auf der
 *  Cluster-Seite. */
export function phaseVon(a: { kind: string; phase: string; reason?: string } | undefined | null): Phase | null {
  if (!a) return null;
  const upgrade = a.kind === "upgrade";
  switch (a.phase) {
    case "triggered":
      return { label: upgrade ? "Upgrade läuft…" : "wird ausgelöst…", art: "laeuft", upgrade };
    case "sent":
      return { label: "startet neu…", art: "laeuft", upgrade };
    case "down":
      // Unten zu sein gehört zum Neustart; „noch nicht zurück" klang nach
      // Störung (Tim testete 29.09. an Network).
      return { label: "fährt hoch…", art: "laeuft", upgrade };
    case "recovered":
      return { label: "kommt zurück…", art: "laeuft", upgrade };
    case "settled":
      return { label: upgrade ? "Upgrade fertig" : "Neustart fertig", art: "fertig", upgrade };
    case "failed":
      return { label: a.reason || "fehlgeschlagen", art: "danger", upgrade };
    default:
      return { label: a.phase, art: "laeuft", upgrade };
  }
}

/** Schläft der Rechner gerade? Nur für Rechner, bei denen Schlafen normal
 *  ist (sleep_ok oder Override). Dann gilt auch „online, aber SSH antwortet
 *  nicht" als Schlaf: nach einem Neustart des Dienstes steht die letzte
 *  Meldung des Agents noch auf online, der schlafende Mac nimmt aber kein
 *  SSH an (29.09.: Cluster sagte „SSH tot", der Baum „kein SSH"; Tim: „Der
 *  schläft ja und das sollte kein Problem sein"). */
export function schlaeftGerade(h: Pick<ClusterStatsHost, "online" | "ssh_reachable">, schlaeft: boolean): boolean {
  return schlaeft && (h.online !== true || h.ssh_reachable === false);
}

/** Noch nie gemeldet -- das Urteil fällt im Monitor (nieGemeldet in
 *  internal/cluster/monitor.go: kein Agent, der je gemeldet hat, nicht im
 *  Anlauf nach dem Neustart). Zählt im Lage-Satz nicht zu „online". */
export function nochNichtInBetrieb(h: Partial<Pick<ClusterStatsHost, "noch_nicht_in_betrieb">>): boolean {
  return h.noch_nicht_in_betrieb === true;
}

// Normales ist leise (docs/gestaltung.md, Regel 2): online ist ein Wort
// ohne Farbe, ein schlafender Rechner und ein stummer Cluster auch. Rot
// heißt offline, Gelb „SSH tot" (lebt, aber Backups und Eingriffe
// scheitern), Akzent eine laufende Aktion.
export function zustandVon(h: Pick<ClusterStatsHost, "online" | "ssh_reachable"> & Partial<Pick<ClusterStatsHost, "stats" | "state" | "noch_nicht_in_betrieb">>, u: Umstaende): RechnerZustand {
  if (u.phase && u.phase.art !== "fertig") return { text: u.phase.label, ton: u.phase.art === "danger" ? "danger" : "accent" };
  // Schlafend auch bei unbekanntem Stand (null): ein Mac, der seit Tagen
  // schläft, hat oft gar keinen frischen Zustand mehr (29.09.: MacBook
  // stand als „unbekannt" da und WOB auf „5 online").
  if (schlaeftGerade(h, u.schlaeft)) return { text: "schläft", ton: "" };
  if (u.stumm) return { text: h.online === false ? "offline, stumm" : "stumm", ton: "" };
  if (h.online === false) return { text: "offline", ton: "danger" };
  // Noch nie gemeldet: kein Agent, keine Werte, nie ein Zustand -- ein
  // vorbereiteter Rechner (Tim 25.10.2026, Router zuhause: „Jetzt
  // vorbereitet, live nach Umzug"). Leise, und Meldungen gibt es dafür
  // ohnehin keine (alerts/checker: Online == nil baut keinen Alarm). Mit der
  // ersten Meldung seines Agents ist er von selbst dabei. Im Anlauf nach
  // einem Neustart des Dienstes urteilt der Monitor noch nicht: dann nur
  // „unbekannt".
  if (nochNichtInBetrieb(h)) return { text: "noch nicht in Betrieb", ton: "" };
  if (h.online == null) return { text: "unbekannt", ton: "" };
  if (h.ssh_reachable === false) return { text: "SSH tot", ton: "warn" };
  if (u.phase?.art === "fertig") return { text: u.phase.label, ton: "" };
  return { text: "online", ton: "ok" };
}

/** Messwerte (CPU, RAM, Temperatur) gelten nur, solange der Rechner
 *  erreichbar ist. Bis 29.09.2026 zeigte das schlafende MacBook 26 % CPU
 *  und 90 % RAM -- die letzte Meldung vor dem Einschlafen, ohne Hinweis
 *  auf ihr Alter (Lesson „Retained ≠ Lebenszeichen"). */
export function messwerteGelten(h: Pick<ClusterStatsHost, "online">): boolean {
  return h.online === true;
}

const liste = (namen: string[]): string =>
  namen.length <= 1 ? (namen[0] ?? "") : `${namen.slice(0, -1).join(", ")} und ${namen[namen.length - 1]}`;

/** Lage-Satz einer Cluster-Sektion: die Ausnahme vorn, der Normalfall ein
 *  Wort. Ein stummer Cluster sagt nur, dass er stumm ist. */
export function clusterLage(
  hosts: readonly ClusterStatsHost[],
  umstaende: (h: ClusterStatsHost) => Umstaende,
  stumm: { an: boolean; bis?: string },
): LageTeil[] {
  if (stumm.an) return [{ text: stumm.bis ? `stummgeschaltet bis ${stumm.bis}` : "stummgeschaltet" }];
  const teile: LageTeil[] = [];
  for (const h of hosts) {
    const z = zustandVon(h, umstaende(h));
    if (z.ton && z.ton !== "ok") teile.push({ text: `${h.name} ${z.text}`, ton: z.ton });
  }
  const mitUpdates = hosts.filter((h) => (h.updates?.pending ?? 0) > 0);
  if (mitUpdates.length) {
    const p = mitUpdates.reduce((a, h) => a + (h.updates?.pending ?? 0), 0);
    const s = mitUpdates.reduce((a, h) => a + (h.updates?.security_pending ?? 0), 0);
    const wo = mitUpdates.length === 1 ? mitUpdates[0].name : `${mitUpdates.length} Rechnern`;
    const text = mitUpdates.length === 1
      ? `${wo}: ${p} ${p === 1 ? "Update" : "Updates"}${s ? `, ${s} Sicherheit` : ""}`
      : `${p} Updates auf ${wo}${s ? `, ${s} Sicherheit` : ""}`;
    // Ein Update ist ein Handgriff, kein Vorfall: Gelb nur mit Sicherheit.
    teile.push(s ? { text, ton: "warn" } : { text });
  }
  const neustart = hosts.filter((h) => h.updates?.reboot_required);
  if (neustart.length) teile.push({ text: `Neustart nötig: ${liste(neustart.map((h) => h.name))}`, ton: "warn" });

  const schlafend = hosts.filter((h) => schlaeftGerade(h, umstaende(h).schlaeft));
  const vorbereitet = hosts.filter((h) => nochNichtInBetrieb(h) && !schlaeftGerade(h, umstaende(h).schlaeft));
  const wach = hosts.length - schlafend.length - vorbereitet.length;
  const online = hosts.filter((h) => h.online === true && !schlaeftGerade(h, umstaende(h).schlaeft)).length;
  teile.push({ text: online === wach ? "alle online" : `${online} online`, ton: "ok" });
  if (schlafend.length) teile.push({ text: `${liste(schlafend.map((h) => h.name))} ${schlafend.length === 1 ? "schläft" : "schlafen"}` });
  if (vorbereitet.length) teile.push({ text: `${liste(vorbereitet.map((h) => h.name))} noch nicht in Betrieb` });
  const zurueck = hosts.reduce((a, h) => a + (h.updates?.deferred ?? 0), 0);
  if (zurueck) teile.push({ text: `${zurueck} zurückgestellt` });
  return teile;
}

/** Laufzeit seit dem Start: „2 T 4 h", „5 h 12 min". */
export function laufzeit(s: number | null | undefined): string {
  if (s == null) return "—";
  const t = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  if (t > 0) return `${t} T ${h} h`;
  return `${h} h ${Math.floor((s % 3600) / 60)} min`;
}
